import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

/** API data point */
export interface SocKwApiPoint {
  ts: string;
  kw: number;
  soc: number;
}

/** Chart formatted point */
interface ChartPoint {
  time: string;
  power: number;
  soc: number;
}

/** Component Props */
interface ChargingSpeedSocChartProperties {
  data: SocKwApiPoint[];
  available?: boolean;
}

/** Label renderer props */
interface ReferenceLabelProperties {
  // eslint-disable-next-line react/require-default-props
  viewBox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  value: string;
  // eslint-disable-next-line react/require-default-props
  variant?: 'start' | 'stop';
}

/**
 * Formats ISO timestamp → HH:mm
 */
const formatTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Custom Tooltip
 */
function CustomTooltip(properties: TooltipProps<ValueType, NameType>): JSX.Element | null {
  const { t } = useTranslation('charger');
  const { active, payload } = properties as TooltipProps<ValueType, NameType> & {
    payload?: any[];
  };

  if (!active || !payload?.length) return null;


  const { time, power, soc } = payload[0].payload as ChartPoint;

  return (
    <div className="charging-tooltip">
      <p className="time">{time}</p>
      <p className="power">
        {t('chargingSpeed')} {(power ?? 0).toFixed(1)} {t('kw')}
      </p>
      <p className="soc">
        {t('soc')} : {(soc ?? 0).toFixed(1)} %
      </p>
    </div>
  );
}

/**
 * Custom Reference Label
 */
function ReferenceLabel({
  viewBox,
  value,
  variant = 'start',
}: ReferenceLabelProperties): JSX.Element | null {
  if (!viewBox) return null;

  const { x, y, width } = viewBox;
  const labelWidth = 64;
  const labelHeight = 28;
  const paddingRight = 8;
  const bgColor = variant === 'start' ? '#22C55E' : '#EF4444';

  return (
    <g transform={`translate(${x + width - labelWidth - paddingRight}, ${y - labelHeight / 2})`}>
      <rect width={labelWidth} height={labelHeight} rx={14} ry={14} fill={bgColor} />
      <text
        x={labelWidth / 2}
        y={labelHeight / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#ffffff"
        fontSize={12}
        fontWeight={600}>
        {value}
      </text>
    </g>
  );
}

/**
 * Charging Speed vs SOC Chart
 */
function ChargingSpeedSocChart({ data, available }: ChargingSpeedSocChartProperties): JSX.Element {
  const { t } = useTranslation('charger');

  /**
   * Transform API data → chart format
   */
  const chartData: ChartPoint[] = useMemo(
    () =>
      data.map((item) => ({
        time: formatTime(item.ts),
        power: item.kw,
        soc: item.soc,
      })),
    [data],
  );

  if (!chartData.length) {
    return (
      <div className="no-charging-data">
        <p> </p>
      </div>
    );
  }

  const startPower = chartData[0].power;
  const stopPower = chartData[chartData.length - 1].power;
  const stopTime = chartData[chartData.length - 1].time;

  return (
    <div className="charging-chart-wrapper">
      <div className="heading">
        <p className="title">{t('socChargingSpeedGraph')}</p>
        <div className="sub-title">
          <p className="title-kw">
            <div className="green" /> {t('kw')}
          </p>
          <p className="title-soc" style={{ color: '#0099D6' }}>
            <div className="green" style={{ backgroundColor: '#0099D6' }} />
            {t('socPercent')}
          </p>
        </div>
      </div>

      <div className="content">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <XAxis dataKey="time" tickLine={false} axisLine={false} />

            <YAxis
              yAxisId="left"
              domain={[0, 'auto']}
              tickFormatter={(value: number) => `${value} ${t('kw')}`}
              tickLine={false}
              axisLine={false}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 100]}
              tickFormatter={(value: number) => `${value}%`}
              tickLine={false}
              axisLine={false}
            />

            <Tooltip content={<CustomTooltip />} />
            {!available && (
              <>
                {' '}
                <ReferenceLine
                  y={startPower}
                  yAxisId="left"
                  stroke="#22C55E"
                  strokeDasharray="3 3"
                  label={<ReferenceLabel value={t('chartStart')} variant="start" />}
                />
                <ReferenceLine
                  y={stopPower}
                  yAxisId="left"
                  stroke="#EF4444"
                  strokeDasharray="3 3"
                  label={<ReferenceLabel value={t('chartStop')} variant="stop" />}
                />
              </>
            )}

            {/* STOP TIME VERTICAL LINE (FIXED) */}
            <ReferenceLine
              x={stopTime}
              yAxisId="left" // ✅ THIS FIXES THE CRASH
              stroke="#00B3A4"
              strokeDasharray="4 4"
            />

            {/* CENTER DOT */}
            <ReferenceDot
              x={stopTime}
              y={stopPower}
              yAxisId="left"
              r={12}
              fill="#E6FFFA"
              stroke="#00B3A4"
              strokeWidth={2}
              label={{ value: '⚡', position: 'center', fontSize: 14 }}
            />

            {/* POWER LINE */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="power"
              stroke="#2E9E3A"
              strokeWidth={3}
              dot={false}
            />

            {/* SOC LINE */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="soc"
              stroke="#0088FE"
              strokeWidth={3}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

ChargingSpeedSocChart.defaultProps = {
  available: false,
};

export default ChargingSpeedSocChart;
